// src/components/shared/Pagination.jsx
import React from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const visiblePageCount = 3;

    if (currentPage - Math.floor(visiblePageCount / 2) > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
        >
          1
        </button>
      );

      if (currentPage - Math.floor(visiblePageCount / 2) > 2) {
        pages.push(<span key="left-ellipsis" className="px-2">...</span>);
      }
    }

    let startPage = Math.max(1, currentPage - Math.floor(visiblePageCount / 2));
    let endPage = Math.min(totalPages, currentPage + Math.floor(visiblePageCount / 2));

    if (currentPage <= Math.floor(visiblePageCount / 2)) {
      endPage = Math.min(visiblePageCount, totalPages);
    }
    if (currentPage >= totalPages - Math.floor(visiblePageCount / 2)) {
      startPage = Math.max(1, totalPages - visiblePageCount + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded ${currentPage === i ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
        >
          {i}
        </button>
      );
    }

    if (currentPage + Math.floor(visiblePageCount / 2) < totalPages) {
      if (currentPage + Math.floor(visiblePageCount / 2) < totalPages - 1) {
        pages.push(<span key="right-ellipsis" className="px-2">...</span>);
      }

      if (endPage < totalPages) {
        pages.push(
          <button
            key={totalPages}
            onClick={() => handlePageChange(totalPages)}
            className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
          >
            {totalPages}
          </button>
        );
      }
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-center border-t border-gray-200 bg-white px-4 py-3">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <span className="sr-only">{t("Previous")}</span>
          <i className="fas fa-chevron-left"></i>
        </button>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <span className="sr-only">{t("Next")}</span>
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>

      <div className="hidden sm:flex sm:items-center sm:justify-center">
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label={t("Pagination")}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              <span className="sr-only">{t("Previous")}</span>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            {renderPageNumbers()}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              <span className="sr-only">{t("Next")}</span>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;